/**
 * Physics-Based Splash Screen URL Mode
 * 
 * Real 2D physics simulation that reveals "pivot (alpha) 0.0.1" text in the URL hash.
 * Uses spring physics to simulate water waves with realistic motion.
 */

import { SPLASH_SCREEN_TEXT, SPLASH_SCREEN_LENGTH, SPLASH_SCREEN_BLOCKS } from './splash-screen.js';

export interface PhysicsSplashScreenState {
  active: boolean;
  startTime: number;
  points: Array<{ x: number; y: number; v: number }>;
  pointCount: number;
  targetY: number;
  translationDelay: number;
  translationDuration: number;
  revealHeight: number;
  hasBeenRevealed: boolean; // Track if logo has been revealed once
}

/**
 * Create physics splash screen state
 */
export function createPhysicsSplashScreenState(): PhysicsSplashScreenState {
  const pointCount = SPLASH_SCREEN_LENGTH;
  const canvasHeight = 300; // Virtual canvas height for calculations
  const startY = canvasHeight / 4; // Start at 25% from top (75% full)
  
  // Initialize points at starting height
  const points = Array.from({ length: pointCount }, (_, i) => ({
    x: i / (pointCount - 1), // Normalized x position (0 to 1)
    y: startY,
    v: 0, // Velocity
  }));

  // Apply random impact at start with much higher force
  // Use a more random distribution to ensure truly random location
  const randomIndex = Math.floor(Math.random() * pointCount);
  if (points[randomIndex]) {
    // Much higher impact force for more dramatic effect
    points[randomIndex].v = -1000; // Very large initial impact
  }

  return {
    active: true,
    startTime: performance.now(),
    points,
    pointCount,
    targetY: startY,
    translationDelay: 4000, // 4 second delay before drop starts
    translationDuration: 6000, // 6 seconds to complete drop
    revealHeight: 0, // Reveal at lowest block level
    hasBeenRevealed: false, // Logo has not been revealed yet
  };
}

/**
 * Generate physics-based splash screen string
 */
export function generatePhysicsSplashScreen(state: PhysicsSplashScreenState): string {
  if (!state.active) {
    return '';
  }

  const elapsed = performance.now() - state.startTime;
  const totalDuration = 15000; // 15 seconds total animation

  // End animation after total duration
  if (elapsed >= totalDuration) {
    state.active = false;
    return '';
  }

  const blocks = SPLASH_SCREEN_BLOCKS;
  const pivotText = SPLASH_SCREEN_TEXT;
  const pivotLength = pivotText.length;
  const canvasHeight = 300;
  
  // Replace spaces in pivot text with lowest block character for spacing (like original)
  const pivotTextWithBlocks = pivotText.replace(/ /g, blocks[0]);
  
  // Create revealed text that matches the full pointCount length
  // Center the pivot text and pad with blocks to match pointCount
  const paddingLength = Math.max(0, state.pointCount - pivotLength);
  const leftPadding = Math.floor(paddingLength / 2);
  const rightPadding = paddingLength - leftPadding;
  const revealedText = blocks[0].repeat(leftPadding) + pivotTextWithBlocks + blocks[0].repeat(rightPadding);

  // Physics parameters
  const k = 0.03; // Spring constant
  const damp = 0.97; // Damping
  const spread = 0.35; // Wave spread
  const speed = 1.0; // Speed multiplier

  // Update target Y position based on elapsed time
  if (elapsed >= state.translationDelay) {
    const translationProgress = Math.min(
      (elapsed - state.translationDelay) / state.translationDuration,
      1
    );
    // Smooth ease-out interpolation
    const easeOut = 1 - Math.pow(1 - translationProgress, 3);
    // Transition from h1/4 to h1 (moving downward)
    state.targetY = canvasHeight / 4 + (canvasHeight * 3 / 4) * easeOut;
  }

  // Update physics simulation
  const points = state.points;
  
  // Apply spring physics to reach target
  for (let i = 0; i < points.length; i++) {
    const dy = state.targetY - points[i].y;
    points[i].v += dy * k * speed;
    points[i].v *= damp;
  }

  // Apply spread between adjacent points
  for (let i = 0; i < points.length; i++) {
    if (i > 0) {
      points[i - 1].v += (points[i].y - points[i - 1].y) * spread * speed;
    }
    if (i < points.length - 1) {
      points[i + 1].v += (points[i].y - points[i + 1].y) * spread * speed;
    }
  }

  // Update positions
  for (const p of points) {
    p.y += p.v * speed;
  }

  // Calculate center position for pivot text
  const centerIndex = Math.floor(points.length / 2);
  const pivotStartIndex = Math.max(0, centerIndex - Math.floor(pivotLength / 2));
  const pivotEndIndex = Math.min(points.length, pivotStartIndex + pivotLength);

  // Check if points in pivot range are at or below reveal height
  // Once revealed, keep it revealed permanently
  let shouldShowPivot = state.hasBeenRevealed;
  
  if (!shouldShowPivot && points.length >= pivotLength) {
    // Check only actual text characters (not block separators/spaces)
    shouldShowPivot = true;
    for (let i = 0; i < pivotText.length; i++) {
      if (pivotText[i] === ' ') continue; // Skip spaces
      
      const pointIndex = pivotStartIndex + i;
      if (pointIndex >= points.length || !points[pointIndex]) continue;
      
      const heightFromBottom = canvasHeight - points[pointIndex].y;
      const normalized = Math.max(0, Math.min(1, heightFromBottom / canvasHeight));
      const blockIndex = Math.floor(normalized * 8);
      
      if (blockIndex > state.revealHeight) {
        shouldShowPivot = false;
        break;
      }
    }
    
    // Mark as revealed once it's been revealed
    if (shouldShowPivot) {
      state.hasBeenRevealed = true;
    }
  }

  // Map each point's y position to a block character
  const blockArray = points.map((p, i) => {
    if (!p) return blocks[0];

    // If we should show pivot, return the character from revealed text (same length as pointCount)
    if (shouldShowPivot && i < revealedText.length) {
      return revealedText[i];
    }

    // Otherwise, calculate block character based on height
    const heightFromBottom = canvasHeight - p.y;
    const normalized = Math.max(0, Math.min(1, heightFromBottom / canvasHeight));
    const blockIndex = Math.floor(normalized * 8);
    return blocks[Math.min(7, Math.max(0, blockIndex))];
  });

  return blockArray.join('');
}

